
// Helper function to determine if an operator requires a value
export const operatorRequiresValue = (operator: string): boolean => {
    const noValueOperators = ['isnull', 'isnotnull'];
    return !noValueOperators.includes(operator);
};

// Helper function to determine if an operator requires a regex pattern
export const requiresRegexPattern = (operator: string): boolean => {
    return ['regex', 'iregex'].includes(operator);
};

// Helper function to determine if an operator is date/time specific
export const isDateTimeOperator = (operator: string): boolean => {
    return ['year', 'month', 'day', 'week_day', 'iso_week_day', 'week', 'iso_year', 'quarter', 'time', 'hour', 'minute', 'second', 'date'].includes(operator);
};

// Helper function to validate a single value based on field type and operator
export const validateSingleValue = (value: any, fieldType: string, operator: string): boolean => {
    // Type-specific validations
    switch (fieldType) {
        case 'IntegerField':
        case 'BigAutoField':
        case 'AutoField':
        case 'SmallIntegerField':
        case 'PositiveIntegerField':
        case 'PositiveSmallIntegerField':
            // For integer fields, value must be a valid integer
            if (operator === 'len') {
                return Number.isInteger(Number(value)) && Number(value) >= 0;
            }
            return Number.isInteger(Number(value));

        case 'FloatField':
        case 'DecimalField':
            // For float/decimal fields, value must be a valid number
            return !isNaN(parseFloat(value as string));

        case 'DateField':
            // For date fields, value must be a valid date
            if (isDateTimeOperator(operator)) {
                // For date part operators, validate based on the specific part
                switch (operator) {
                    case 'year':
                    case 'iso_year':
                        return Number.isInteger(Number(value));
                    case 'month':
                    case 'quarter':
                        return Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= (operator === 'month' ? 12 : 4);
                    case 'day':
                        return Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 31;
                    case 'week':
                        return Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 53;
                    case 'week_day':
                        return Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 6;
                    case 'iso_week_day':
                        return Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 7;
                    default:
                        return true;
                }
            }
            return Boolean(Date.parse(value as string));

        case 'DateTimeField':
            // For datetime fields
            if (isDateTimeOperator(operator)) {
                // Similar validation as DateField for date parts
                if (['year', 'month', 'day', 'week', 'week_day', 'iso_week_day', 'iso_year', 'quarter'].includes(operator)) {
                    return validateSingleValue(value, 'DateField', operator);
                }
                // Time part validations
                switch (operator) {
                    case 'hour':
                        return Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 23;
                    case 'minute':
                    case 'second':
                        return Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 59;
                    default:
                        return true;
                }
            }
            return Boolean(Date.parse(value as string));

        case 'TimeField':
            // For time fields
            if (operator === 'hour' || operator === 'minute' || operator === 'second') {
                return validateSingleValue(value, 'DateTimeField', operator);
            }
            // Try to parse as time
            try {
                const [hours, minutes, seconds] = value.split(':').map(Number);
                return (
                    Number.isInteger(hours) && hours >= 0 && hours <= 23 &&
                    Number.isInteger(minutes) && minutes >= 0 && minutes <= 59 &&
                    (seconds === undefined || (Number.isInteger(seconds) && seconds >= 0 && seconds <= 59))
                );
            } catch {
                return false;
            }

        case 'BooleanField':
            // For boolean fields, value must be a boolean or convertible to boolean
            return value === true || value === false || value === 'true' || value === 'false' || value === 1 || value === 0;

        case 'JSONField':
            // For JSON fields with specific operators
            if (operator === 'has_key' || operator === 'has_keys' || operator === 'has_any_keys') {
                return typeof value === 'string' && value.trim() !== '';
            }
            // For other JSON operators, try to validate as JSON
            if (typeof value === 'string') {
                try {
                    JSON.parse(value);
                    return true;
                } catch {
                    // If it's not valid JSON, it might be a key name
                    return value.trim() !== '';
                }
            }
            return true;

        case 'CharField':
        case 'TextField':
            // For text fields with regex operators
            if (requiresRegexPattern(operator)) {
                try {
                    new RegExp(value);
                    return true;
                } catch {
                    return false;
                }
            }
            // For len operator, must be a non-negative integer
            if (operator === 'len') {
                return Number.isInteger(Number(value)) && Number(value) >= 0;
            }
            return true;

        default:
            return true;
    }
};



// Helper function to validate input based on field type and operator
export const validateInput = (value: any, fieldType: string, operator: string): boolean => {
    // No validation needed for operators that don't require values
    if (!operatorRequiresValue(operator)) return true;

    // Empty check (except for specific operators that can accept empty strings)
    if (value === null || value === undefined || value === '') {
        // Some operators like 'contains' can accept empty strings
        if (['contains', 'icontains', 'startswith', 'istartswith', 'endswith', 'iendswith'].includes(operator)) {
            return true;
        }
        return false;
    }

    // For array values (used in range, in, etc.)
    if (Array.isArray(value)) {
        if (value.length === 0) return false;

        // For range operators, need exactly 2 values
        if (operator === 'range' && value.length !== 2) return false;

        // Validate each value in the array based on field type
        return value.every(v => validateSingleValue(v, fieldType, operator));
    }

    return validateSingleValue(value, fieldType, operator);
};

// Helper function to get appropriate input type based on field type
export const getInputTypeForFieldType = (fieldType: string): string => {
    switch (fieldType) {
        case 'IntegerField':
        case 'BigAutoField':
        case 'SmallIntegerField':
        case 'PositiveIntegerField':
        case 'PositiveSmallIntegerField':
            return 'number';
        case 'FloatField':
        case 'DecimalField':
            return 'number';
        case 'DateField':
            return 'date';
        case 'DateTimeField':
            return 'datetime-local';
        case 'TimeField':
            return 'time';
        case 'BooleanField':
            return 'checkbox';
        default:
            return 'text';
    }
};
